import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Column, Row, Typography } from '@tempmee/react';
import { usePromoCode, usePromoCodeReport } from '../../../domain/Campaigns';
import { useDsoNameResolver } from '../../../hooks/useDsoNameResolver';
import { PromoCodeBreadcrumbs } from './components/PromoCodeBreadcrumbs';
import { PromoCodeDetailsCard } from './components/PromoCodeDetailsCard';
import { PromoCodeStatusLabel } from './components/PromoCodeStatusLabel';
import { PromoCodeTargetingCard } from './components/PromoCodeTargetingCard';
import { PromoCodeTargetOfficesDialog } from './components/PromoCodeTargetOfficesDialog';

/**
 * Promo Codes → one code, at `/promo-codes/:campaignId` (GOTU-5661): its
 * details, uptake and targeting. View-only for V2 — the Figma's Edit control
 * is a later ticket.
 */
export const PromoCodeDetail = () => {
  const { campaignId } = useParams();

  const { promoCode, isPending, isError } = usePromoCode(campaignId);
  // Held until the code itself has loaded: an id that 404s on the code would
  // 404 here too, and there is no point asking twice.
  const { report } = usePromoCodeReport(campaignId, { enabled: Boolean(promoCode) });
  const resolveDsoName = useDsoNameResolver();
  const [isOfficeListOpen, setIsOfficeListOpen] = useState(false);

  if (isPending) {
    return (
      <Column px={8} pt={6} gap={5}>
        <Typography className="text-text-secondary">Loading promo code…</Typography>
      </Column>
    );
  }

  // Two different dead ends, kept distinct: a 404 resolves to null and means
  // the id is gone or mistyped, while a 400/403/500 is a genuine failure that
  // must not be reported as "not found". Either way, always leave a way back.
  if (isError || !promoCode) {
    return (
      <Column px={8} pt={6} gap={4}>
        <PromoCodeBreadcrumbs />
        <Typography>
          {isError ? 'Could not load this promo code.' : 'Promo code not found.'}
        </Typography>
      </Column>
    );
  }

  const {
    professions, postingTypeLabels, states, metros, dsoCodes, officeIds,
  } = promoCode.targeting;
  const dsos = dsoCodes.map((code) => ({ code, name: resolveDsoName(code) }));

  return (
    <Column px={8} pt={4} pb={12} gap={5}>
      <Column gap={3}>
        <PromoCodeBreadcrumbs codeName={promoCode.name} />

        <Row align="center" gap={3} className="min-w-0">
          <Typography variant="h5" as="h1" numberOfLines={1}>
            {promoCode.name}
          </Typography>
          <PromoCodeStatusLabel status={promoCode.status} label={promoCode.statusLabel} />
          {/* The design titles the page with the code itself (its name and code
              coincide there). Ours is the campaign name, so the code — what an
              office actually types — rides beside it; an automatic campaign
              has none and shows nothing. */}
          {promoCode.code ? (
            <Typography variant="body1" className="text-text-secondary">
              {promoCode.code}
            </Typography>
          ) : null}
        </Row>
      </Column>

      {/* Two cards side by side as in the Figma, stacking when the shell's
          content column is too narrow for both. */}
      <Row gap={4} wrap="wrap" align="start">
        <PromoCodeDetailsCard promoCode={promoCode} report={report} />
        <PromoCodeTargetingCard
          professions={professions}
          postingTypes={postingTypeLabels}
          states={states}
          metros={metros}
          dsos={dsos}
          onViewOffices={officeIds.length > 0 ? () => setIsOfficeListOpen(true) : null}
        />
      </Row>

      {/* Mounted only while open, so each opening reads page 1 with no search
          and nothing is fetched until an admin asks. */}
      {isOfficeListOpen ? (
        <PromoCodeTargetOfficesDialog
          open
          onClose={() => setIsOfficeListOpen(false)}
          campaignId={campaignId}
        />
      ) : null}
    </Column>
  );
};

export default PromoCodeDetail;
